import React, { useState, useRef, useEffect } from 'react'
import { FieldRenderProps } from 'react-final-form'
import arrowDown from 'assets/images/arrowDown.svg'
import calender from 'assets/images/calender.svg'
import card from 'assets/images/card.svg'
import Select, { StylesConfig } from 'react-select'
import { useTranslation } from 'react-i18next'
import './Inputs.scss'
import { imageRegex } from 'redux/_helpers/Constants'
import CN from 'classnames'
import closeEye from 'assets/images/closeEye.svg'
import openEye from 'assets/images/openEye.svg'
import flatpickr from 'flatpickr'
import type { Instance } from 'flatpickr/dist/types/instance'

type Props = FieldRenderProps<string, any>

interface SelectProps extends FieldRenderProps<MyOptionType, any> {
  textField: string
  valueField: string
  menuBg?: string
  border?: string
  placeholderColor?: string
  validate?: any | undefined
}

interface Variant {
  [key: string]: string
}

type InputProps = {
  name: string
  invert?: boolean
  value: string
  id: string
  label: string
  disabled: boolean
  onChange: (
    e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>
  ) => void
  placeholder?: string
  filledInput?: string
  className: string
}

export function FileInputField(props: Props): JSX.Element {
  const { t } = useTranslation()
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [fileName, setFileName] = useState<string | null>(null)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setFileName(file.name)
      props.input.onChange(file)
    }
  }

  const handleDelete = () => {
    setFileName(null)
    props.input.onChange(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  return (
    <div className="relative input">
      <label htmlFor={props.input.name} className="w-full text-xs text-gray-800">
        {t(props.label)}
      </label>
      <input
        type="file"
        id={props.input.name}
        name={props.input.name}
        onChange={handleFileChange}
        ref={fileInputRef}
        style={{ display: 'none' }}
        disabled={props.disabled}
      />
      <div className="flex items-center mt-2">
        <button
          type="button"
          onClick={() => fileInputRef.current && fileInputRef.current.click()}
          className="w-full h-8 border border-gray-300 rounded-lg p-2 font-light text-xs bg-white"
          disabled={props.disabled}>
          {fileName || t(props.placeholder)}
        </button>
        {fileName && (
          <button type="button" onClick={handleDelete} className="ml-2 text-xs text-red-600">
            {t('Delete')}
          </button>
        )}
      </div>
      {props.meta.error && props.meta.touched && (
        <p className="mt-1 text-xs text-red-700">{t(props.meta.error)}</p>
      )}
    </div>
  )
}
export function InputField(props: Props): JSX.Element {
  const { t } = useTranslation() // ti18n
  const [hidePassword, setHidePassword] = useState<boolean>(false)
  return (
    <div className=" relative input">
      <label
        htmlFor={`${props.input?.name}`}
        className={`w-full  text-xs text-gray-800		${props.labelClass && props.labelClass}`}>
        {t(props.label)}
      </label>
      {props.input.type === 'password' ? (
        <input
          {...props.input}
          {...props.rest}
          id={props.input?.name}
          min={props.min}
          ref={props.input.ref}
          type={!hidePassword ? 'password' : 'text'}
          className={`w-full  h-8 border border-gray-300 rounded-lg p-2 font-light text-xs  ${props.className}`}
          placeholder={t(props.placeholder)}
          disabled={props.disabled}
          autoComplete="off"
        />
      ) : (
        <input
          {...props.input}
          {...props.rest}
          ref={props.input.ref}
          id={props.input?.name}
          min={props.min}
          className={`w-full h-8 border  rounded-lg p-2 font-light text-xs  ${props.className}`}
          placeholder={t(props.placeholder)}
          disabled={props.disabled}
        />
      )}
      {props.input.type === 'password' && (
        <button
          type="button"
          onClick={() => setHidePassword(!hidePassword)}
          className="absolute ltr:right-4 rtl:left-4  ">
          <img
            src={hidePassword ? openEye : closeEye}
            alt="hidePassword"
            className="w-[25px] h-[33px] mr-[-10px] object-contain mx-1"
          />
        </button>
      )}
      {props?.meta?.error && props.meta.touched && props?.meta?.error && (
        <p className="mt-1 text-xs text-red-700">{t(props?.meta.error)}</p>
      )}
    </div>
  )
}
function DropdownIndicator() {
  return (
    <div className="w-full px-2 flex items-center justify-center">
      <img src={arrowDown} alt="arrowDownCP" className="mx-1 w-[15px]" />
    </div>
  )
}

interface MyOptionType extends Variant {
  id: string
  value: string
}
function SelectInput(props: SelectProps): JSX.Element {
  const { t } = useTranslation()
  const customStyles: StylesConfig<MyOptionType, boolean> = {
    menu: (provided, state) => ({
      ...provided,
      padding: 10,
      zIndex: 1000,
      fontSize: '12px',
      fontWeight: 300,
      backgroundColor: '#fff',
    }),
    // wrapperStyle: (provided, state) => ({
    //   ...provided,
    //   backgroundColor: ' #eeeeee',
    // }),
    control: (_, state) => ({
      borderRadius: '0.50rem',
      opacity: state.isDisabled ? 0.5 : 1,
      border: '1px solid #d1d5db',
      boxShadow: 'none',
      backgroundSize: '20px',
      height: '32px',
      backgroundRepeat: ' no-repeat',
      backgroundImage: arrowDown,
      backgroundPosition: '7px center',
      fontSize: '12px',
      fontWeight: 300,
      display: 'flex',
      '::placeholder': { color: '#1f2937', fontSize: '12px' },
      // padding: '0.25rem',
    }),
    container: (_, state) => ({
      width: '100%',
      backgroundColor: 'transparent',
    }),
    singleValue: (provided, state) => ({
      ...provided,
      color: '#1f2937',
      '::placeholder': { color: props.placeholderColor },
    }),
    placeholder: (provided, state) => ({
      ...provided,
      color: '#1f2937',
      fontSize: '12px',
    }),
  }
  const lg = localStorage.getItem('language')
  const valueAr = 'ValueAr'
  return (
    <div className=" relative dropDownList">
      <label htmlFor={`${props.input?.name}`} className="w-full text-xs text-gray-800 mb-1">
        {t(props.label)}
      </label>
      {props.isMulti ? (
        <Select
          {...props.input}
          isMulti
          styles={customStyles}
          options={props.options}
          className="dropDownList"
          getOptionLabel={(e: any) => (lg === 'ar' ? 'ValueAr' : e[props.textField])}
          getOptionValue={(e: any) => e[props.valueField]}
          name={props?.input?.name}
          id={props?.input?.name}
          components={{ DropdownIndicator }}
          isDisabled={props.disabled}
          placeholder={props.placeholder ? t(`${props.placeholder}`) : `${t(`Select`)}...`}
        />
      ) : (
        <Select
          {...props.input}
          defaultValue={
            props?.input?.value &&
            props.options?.filter((x: any) => x[props.valueField] === props.input.value)
          }
          styles={customStyles}
          className="dropDownList"
          options={props.options}
          getOptionLabel={(e: any) => {
            return lg === 'ar' ? e[valueAr] : e[props.textField]
          }}
          getOptionValue={(e: MyOptionType) => e[props.valueField]}
          name={props?.input?.name}
          id={props?.input?.name}
          components={{ DropdownIndicator }}
          value={
            props.input?.value
              ? props.options?.filter((x: any) => x[props.valueField] === props.input.value)
              : []
          }
          onChange={
            props.onChange
              ? (e: any) => {
                  props.onChange(e !== null ? e[props.valueField] : e)
                  props?.input?.onChange(e !== null ? e[props.valueField] : e)
                }
              : (e: any) => props?.input?.onChange(e !== null ? e[props.valueField] : e)
          }
          isDisabled={props.disabled}
          placeholder={props.placeholder ? t(`${props.placeholder}`) : `${t(`Select`)}...`}
        />
      )}
      {props?.meta?.error && props.meta.touched && props?.meta?.error && (
        <p className="mt-1 text-xs text-red-700">{t(props?.meta.error)}</p>
      )}
    </div>
  )
}
SelectInput.defaultProps = {
  menuBg: '',
  border: '',
  placeholderColor: '',
}
export const SelectField = SelectInput

export const CheckboxField = (props: Props) => {
  const { t } = useTranslation()

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // If there's a custom onChange handler, call it first
    if (props.onChange) {
      props.onChange(event)
    }
    // Update the form field value
    props.input.onChange(event)
  }
  return (
    <div className="w-full text-sm">
      <div className="checkbox flex items-center pointer">
        <input
          {...props?.input}
          className={`border-gray-300 rounded-xl ${props.className ? props.className : ''} `}
          type="checkbox"
          checked={props.checked || undefined}
          onChange={handleChange}
          id={props?.name ? props?.name : undefined}
          disabled={props.disabled}
        />
        <label
          htmlFor={props.input.id}
          className={`pb-1 main-text-color whitespace-nowrap mt-1 ${
            props.disabled ? 'disabled' : ''
          }`}>
          <span
            className={`${
              props.labelClass ? props.labelClass : ''
            } xl:text-base md:text-xs text-base whitespace-now px-1`}>
            {t(props.label ? props.label : '')}
          </span>
        </label>
      </div>
      {props?.meta?.error && props.meta.touched && props?.meta?.error && (
        <p className="mt-1 text-xs text-red-700">{t(props?.meta.error)}</p>
      )}
    </div>
  )
}

export const DateField = (props: Props) => {
  const { t } = useTranslation()

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const dateValue = event.target.value
    // If there's a custom onChange handler, call it first
    if (props.onChange) {
      props.onChange(dateValue)
    }
    // Update the form field value
    props.input.onChange(dateValue)
  }

  return (
    <div className="">
      <label htmlFor={`${props?.input?.name}`} className="w-full text-xs text-gray-800">
        {t(props.label)}
      </label>
      <div className="w-full relative">
        <input
          {...props?.input}
          disabled={props.disabled}
          placeholder={t(props.placeholder)}
          min={props.min}
          max={props.max}
          type="date"
          value={props?.input?.value ? props?.input?.value?.split('T')[0] : props?.input?.value}
          onChange={handleDateChange}
          name={props?.input?.name}
          className={`DateField w-full  h-8 border border-gray-300 rounded-xl p-2 font-light text-xs ${props.className} `}
          id={props?.input?.name}
          autoComplete="off"
        />
      </div>

      {props?.meta?.error && props.meta.touched && props?.meta?.error && (
        <p className="mt-1 text-xs text-red-700">{t(props?.meta.error)}</p>
      )}
    </div>
  )
}

export const TimeField = (props: Props) => {
  const { t } = useTranslation()

  const handleTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const timeValue = event.target.value
    const dateValue = props.input.value
      ? props.input.value.split('T')[0]
      : new Date().toISOString().split('T')[0] // Default to today if no date is set
    props.input.onChange(`${timeValue}`)
  }
  return (
    <div className="">
      <div className="flex w-full relative">
        <div className="" />
        <div className="w-full">
          <label htmlFor={`${props.input.name}-time`} className="text-xs text-gray-800">
            {t(props.label)}
          </label>
          <div className="w-full relative">
            <input
              type="time"
              value={props.input.value ? props.input.value : '00:00'} // Default to midnight if no time is set
              onChange={handleTimeChange}
              className={`DateField w-full h-8 border border-gray-300 rounded-xl p-2 font-light text-xs ${props.className}`}
              id={`${props.input.name}-time`}
              autoComplete="off"
            />
          </div>
        </div>
      </div>
      {props.meta.error && props.meta.touched && (
        <p className="mt-1 text-xs text-red-700">{t(props.meta.error)}</p>
      )}
    </div>
  )
}
// export const CCVField = (props: Props) => {
//   const { t } = useTranslation() // ti18n
//   return (
//     <div className="w-full flex flex-wrap items-end">
//       {(props.filledInput && props.placeholder && (props.input?.value || props.value)) || props.label ? (
//         <label className="w-full text-sm font-bold" htmlFor={props.name}>
//           {t(props.placeholder) || t(props.label)}
//         </label>
//       ) : (
//         ''
//       )}
//       <div className="w-full relative">
//         <input
//           {...props.input}
//           {...props.rest}
//           className={`w-full h-10 border rounded p-2 py-1 disabled:opacity-50 focus-visible:outline-0 ${props.className}`}
//           placeholder={t(props.placeholder)}
//           disabled={props.disabled}
//         />
//         <div className=" absolute right-3 top-3  z-10">
//           <img src={card} alt="card" className=" bg-white " height={18} />
//         </div>
//       </div>
//     </div>
//   )
// }

export const RadioButton: React.FC<Props> = (props: Props) => {
  return (
    <div className="RadioButton">
      <input
        type="radio"
        name={props.name}
        id={props.id}
        // value={props.value}
        checked={props.checked}
        onChange={
          props.onChange
            ? e => {
                props.onChange(e)
              }
            : props.onChange
        }
      />
    </div>
  )
}

export const DateTimeField = (props: Props) => {
  const { t } = useTranslation()

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const dateValue = event.target.value
    const timeValue = props.input.value ? props.input.value.split('T')[1] : '00:00' // Default to midnight if no time is set
    props.input.onChange(`${dateValue}T${timeValue}`)
  }

  const handleTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const timeValue = event.target.value
    const dateValue = props.input.value
      ? props.input.value.split('T')[0]
      : new Date().toISOString().split('T')[0] // Default to today if no date is set
    props.input.onChange(`${dateValue}T${timeValue}`)
  }

  return (
    <div className="">
      <div className="flex w-full relative">
        <div className="w-1/2">
          <label htmlFor={props.input.name} className="text-xs text-gray-800">
            {t(props.labelDate)}
          </label>
          <div className="w-full relative">
            <input
              {...props.input}
              disabled={props.disabled}
              placeholder={t(props.placeholder)}
              type="date"
              value={props.input.value ? props.input.value.split('T')[0] : ''}
              onChange={handleDateChange}
              className={`DateField w-full h-8 border border-gray-300 rounded-xl p-2 font-light text-xs ${props.className}`}
              id={props.input.name}
              autoComplete="off"
            />
          </div>
        </div>
        <div className="px-1" />
        <div className="w-1/2">
          <label htmlFor={`${props.input.name}-time`} className="text-xs text-gray-800">
            {t(props.labelTime)}
          </label>
          <div className="w-full relative">
            <input
              type="time"
              value={props.input.value ? props.input.value.split('T')[1] : '00:00'} // Default to midnight if no time is set
              onChange={handleTimeChange}
              className={`DateField w-full h-8 border border-gray-300 rounded-xl p-2 font-light text-xs ${props.className}`}
              id={`${props.input.name}-time`}
              autoComplete="off"
            />
          </div>
        </div>
      </div>

      {props.meta.error && props.meta.touched && (
        <p className="mt-1 text-xs text-red-700">{t(props.meta.error)}</p>
      )}
    </div>
  )
}
export const TextareaField = (props: Props) => {
  const { t } = useTranslation() // ti18n

  return (
    <div className="w-full flex flex-wrap items-end  relative">
      <label htmlFor={`${props?.input?.name}`} className="w-full text-xs text-gray-800">
        {t(props.label)}
      </label>
      <textarea
        {...props.input}
        {...props.rest}
        className={`w-full border p-2 disabled:opacity-50 focus-visible:outline-0 rounded-xl ${props.className}`}
        placeholder={t(props.placeholder)}
        disabled={props.disabled}
        rows={5}
      />
      {props?.meta?.error && props.meta.touched && props?.meta?.error && (
        <p className=" mt-1 text-xs text-red-700">{t(props?.meta.error)}</p>
      )}
    </div>
  )
}

// eslint-disable-next-line react/display-name
export const ImageField = React.forwardRef((props: Props, ref: any) => {
  const { t } = useTranslation()
  const [error, setError] = useState<boolean>(false)
  const handleImage = (e: any) => {
    const { files } = e.target
    if (files && files[0]) {
      const file = files[0]
      if (imageRegex.test(file.name) && file.size / 1024 / 1024 < 7) {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onloadend = () => {
          props?.input?.onChange(reader.result)
        }
        setError(false)
      } else {
        setError(true)
      }
    }
  }

  return (
    <div className="flex flex-col items-center pt-7">
      <input
        id={props.id}
        // className={props.className}
        onChange={e => handleImage(e)}
        type="file"
        name={props?.input?.name}
        style={{ display: 'none' }}
        ref={ref}
        disabled={props.disabled}
      />
      <button
        type="button"
        className={`w-full ${props.className} bg-purple-100 rounded-xl flex items-center justify-center bg-[#F0EDF5] `}
        onClick={() => ref?.current?.click()}>
        {props?.input?.value ? (
          <div className="w-full    rounded-xl  ">
            <div className="h-[135px]  ">
              <img
                src={
                  props.input.value.startsWith('UploadedFiles')
                    ? `${process.env.REACT_APP_API_KEY}/${props.input.value}`
                    : props.input.value
                }
                alt="Uploaded"
                className="w-full h-full object-cover rounded-t-xl"
              />
            </div>
            <div className="w-full  h-[25px] text-white bg-primary-500  rounded-b-lg text-xs py-1 text-center">
              {t(props.labelChange)}
              {/* {t('Change Photo')} */}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center text-purple-500">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="2"
              stroke="currentColor"
              className="w-10 h-10">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            <span className="text-sm font-medium">
              {/* {t('Upload Company Logo')} */}
              {t(props.label)}
            </span>
          </div>
        )}
      </button>
      {(error || (props?.meta?.error && props.meta.touched)) && (
        <p className="mt-2 text-xs text-red-500">
          {error ? t('Only image files under 7MB allowed') : t(props.meta.error)}
        </p>
      )}
    </div>
  )
})
